/**
 * Utility functions for working with cookies securely
 * These functions ensure cookies are set with proper security attributes
 */

/**
 * Determine the environment for cookie settings
 */
const isProduction = (): boolean => {
  // Check if we're in a production environment
  return process.env.NODE_ENV === 'production' || 
         window.location.hostname !== 'localhost';
};

/**
 * Get the appropriate domain for cookies
 * In production, use the current domain
 * In development, use localhost or null
 */
const getCookieDomain = (): string | undefined => {
  if (!isProduction()) return undefined;
  
  const hostname = window.location.hostname;
  // If it's an IP address, don't set domain
  if (/^[0-9.]+$/.test(hostname)) return undefined;
  
  // For domains like example.com, return .example.com to include subdomains
  const parts = hostname.split('.');
  if (parts.length > 1) {
    return `.${parts.slice(-2).join('.')}`;
  }
  
  return hostname;
};

/**
 * Set a cookie with secure attributes
 * 
 * @param name - The name of the cookie
 * @param value - The value of the cookie
 * @param expiryDays - Number of days until the cookie expires
 * @param path - The cookie path (default: '/')
 * @param domain - Optional domain for the cookie
 * @param httpOnly - Whether the cookie should be HttpOnly (default: true for auth cookies)
 */
export function setCookie(
  name: string, 
  value: string, 
  expiryDays: number = 7, 
  path: string = '/',
  domain?: string,
  httpOnly: boolean = false // Never set HttpOnly from client-side JavaScript
): void {
  try {
    const date = new Date();
    date.setTime(date.getTime() + expiryDays * 24 * 60 * 60 * 1000);
    
    // Add security flags
    // Note: HttpOnly cookies can only be set by the server, not by client-side JavaScript
    // For auth cookies, the server should set them with HttpOnly flag
    const secureFlag = window.location.protocol === 'https:' || isProduction() ? 'Secure;' : '';
    const httpOnlyFlag = httpOnly ? 'HttpOnly;' : '';
    
    // Use provided domain or auto-detect
    const cookieDomain = domain || getCookieDomain();
    const domainFlag = cookieDomain ? `domain=${cookieDomain};` : '';
    
    // Set the cookie with SameSite=Strict to prevent CSRF
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${date.toUTCString()}; path=${path}; ${domainFlag} SameSite=Strict; ${httpOnlyFlag} ${secureFlag}`;
  } catch (error) {
    console.error('Error setting cookie:', error);
  }
}

/**
 * Determine if a cookie is sensitive and should have HttpOnly flag
 * 
 * @param name - The name of the cookie
 * @returns Whether the cookie is sensitive
 */
function isSensitiveCookie(name: string): boolean {
  // List of sensitive cookies that should be HttpOnly
  const sensitiveCookies = [
    'auth', 'token', 'session', 'id', 'user', 'firebase', 'csrf',
    'connect.sid', 'sessionid', 'JSESSIONID', 'PHPSESSID'
  ];
  
  return sensitiveCookies.some(cookieName => 
    name.toLowerCase().includes(cookieName.toLowerCase())
  );
}

/**
 * Get a cookie by name
 * 
 * @param name - The name of the cookie to retrieve
 * @returns The cookie value or null if not found
 */
export function getCookie(name: string): string | null {
  try {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return decodeURIComponent(c.substring(nameEQ.length, c.length));
    }
  } catch (error) {
    console.error('Error reading cookie:', error);
  }
  
  return null;
}

/**
 * Remove a cookie by name
 * 
 * @param name - The name of the cookie to remove
 * @param path - The cookie path (default: '/')
 * @param domain - Optional domain for the cookie
 */
export function removeCookie(
  name: string, 
  path: string = '/',
  domain?: string
): void {
  try {
    const secureFlag = window.location.protocol === 'https:' || isProduction() ? 'Secure;' : '';
    
    // Use provided domain or auto-detect
    const cookieDomain = domain || getCookieDomain();
    const domainFlag = cookieDomain ? `domain=${cookieDomain};` : '';
    
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; ${domainFlag} SameSite=Strict; ${secureFlag}`;
  } catch (error) {
    console.error('Error removing cookie:', error);
  }
}

// Authentication cookie constants
const AUTH_TOKEN_COOKIE = 'auth_token';
const AUTH_REFRESH_COOKIE = 'auth_refresh';
const USER_PREFS_COOKIE = 'user_prefs';

/**
 * Set authentication token in a cookie
 * 
 * @param token - The authentication token
 * @param expiryDays - Number of days until the token expires (default: 1)
 */
export function setAuthToken(token: string, expiryDays: number = 1): void {
  setCookie(AUTH_TOKEN_COOKIE, token, expiryDays);
}

/**
 * Get the authentication token from the cookie
 * 
 * @returns The authentication token or null if not found
 */
export function getAuthToken(): string | null {
  return getCookie(AUTH_TOKEN_COOKIE);
}

/**
 * Remove the authentication token cookie
 */
export function clearAuthToken(): void {
  removeCookie(AUTH_TOKEN_COOKIE);
}

/**
 * Set refresh token in a cookie
 * 
 * @param token - The refresh token
 * @param expiryDays - Number of days until the token expires (default: 30)
 */
export function setRefreshToken(token: string, expiryDays: number = 30): void {
  setCookie(AUTH_REFRESH_COOKIE, token, expiryDays);
}

/**
 * Get the refresh token from the cookie
 * 
 * @returns The refresh token or null if not found
 */
export function getRefreshToken(): string | null {
  return getCookie(AUTH_REFRESH_COOKIE);
}

/**
 * Remove the refresh token cookie
 */
export function clearRefreshToken(): void {
  removeCookie(AUTH_REFRESH_COOKIE);
}

/**
 * Set user preferences in a cookie
 * 
 * @param prefs - User preferences object
 * @param expiryDays - Number of days until the preferences expire (default: 365)
 */
export function setUserPrefs(prefs: object, expiryDays: number = 365): void {
  try {
    setCookie(USER_PREFS_COOKIE, JSON.stringify(prefs), expiryDays);
  } catch (error) {
    console.error('Error saving user preferences:', error);
  }
}

/**
 * Get user preferences from the cookie
 * 
 * @returns The user preferences object or null if not found
 */
export function getUserPrefs(): object | null {
  const prefsStr = getCookie(USER_PREFS_COOKIE);
  if (!prefsStr) return null;
  
  try {
    return JSON.parse(prefsStr);
  } catch (error) {
    console.error('Error parsing user preferences:', error);
    // If there's an error parsing, remove the invalid cookie
    removeCookie(USER_PREFS_COOKIE);
    return null;
  }
}

/**
 * Remove the user preferences cookie
 */
export function clearUserPrefs(): void {
  removeCookie(USER_PREFS_COOKIE);
}

/**
 * Clear all authentication cookies
 */
export function clearAllAuthCookies(): void {
  clearAuthToken();
  clearRefreshToken();
} 